import { z } from 'zod';
import { bounceMetricReadingSchema } from './jobs.js';
export var ReputationTemplateTypeEnum;
(function (ReputationTemplateTypeEnum) {
    ReputationTemplateTypeEnum["FEEDBACK_REQUEST"] = "feedback-request";
    ReputationTemplateTypeEnum["GOOGLE_REVIEW"] = "google-review";
    ReputationTemplateTypeEnum["FEEDBACK_REQUEST_REMINDER"] = "feedback-request-reminder";
    ReputationTemplateTypeEnum["GOOGLE_REVIEW_REMINDER"] = "google-review-reminder";
    ReputationTemplateTypeEnum["COMBINED_REQUEST"] = "combined-request";
    ReputationTemplateTypeEnum["COMBINED_REQUEST_REMINDER"] = "combined-request-reminder";
})(ReputationTemplateTypeEnum || (ReputationTemplateTypeEnum = {}));
/** Single source of truth for business automation statuses */
export var BusinessAutomationStatusEnum;
(function (BusinessAutomationStatusEnum) {
    BusinessAutomationStatusEnum["DRAFT"] = "draft";
    BusinessAutomationStatusEnum["ACTIVE"] = "active";
    BusinessAutomationStatusEnum["RUNNING"] = "running";
    BusinessAutomationStatusEnum["SENDING"] = "sending";
    BusinessAutomationStatusEnum["COMPLETED"] = "completed";
    BusinessAutomationStatusEnum["FAILED"] = "failed";
    BusinessAutomationStatusEnum["CANCELLED"] = "cancelled";
    BusinessAutomationStatusEnum["PARTIALLY_CANCELLED"] = "partially_cancelled";
    BusinessAutomationStatusEnum["ARCHIVED"] = "archived";
})(BusinessAutomationStatusEnum || (BusinessAutomationStatusEnum = {}));
/**
 * Statuses where a tenant has the automation enabled / actively running — the "In Use" set.
 * Distinct from the derived "Active" list-status tier (a template is Active when AT LEAST ONE tenant
 * is in one of these states). Single source of truth for both the backend status aggregation and the
 * frontend tenant-status mapper.
 *
 * Note: the frontend mapper also treats the legacy string `'scheduled'` as enabled, but that is not a
 * `BusinessAutomationStatusEnum` member (the backend never emits it), so it is not listed here.
 */
export const ENABLED_AUTOMATION_STATUSES = [
    BusinessAutomationStatusEnum.ACTIVE,
    BusinessAutomationStatusEnum.RUNNING,
    BusinessAutomationStatusEnum.SENDING,
];
export function isEnabledAutomationStatus(status) {
    return ENABLED_AUTOMATION_STATUSES.includes(status);
}
/** Valid transitions for recurring automations (trigger-based, date-based). Used for backend validation and UI dropdowns. */
export const RECURRING_STATE_TRANSITIONS = {
    [BusinessAutomationStatusEnum.DRAFT]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.SENDING,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.SENDING]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.ACTIVE]: [
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.ARCHIVED]: [BusinessAutomationStatusEnum.DRAFT],
    [BusinessAutomationStatusEnum.RUNNING]: [],
    [BusinessAutomationStatusEnum.COMPLETED]: [],
    [BusinessAutomationStatusEnum.FAILED]: [],
    [BusinessAutomationStatusEnum.CANCELLED]: [],
    [BusinessAutomationStatusEnum.PARTIALLY_CANCELLED]: [],
};
/** Valid transitions for one-time automations (e.g. broadcasts). Backend validation. ARCHIVED is terminal. */
export const ONE_TIME_STATE_TRANSITIONS = {
    [BusinessAutomationStatusEnum.DRAFT]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.SENDING,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.SENDING]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.PARTIALLY_CANCELLED,
    ],
    [BusinessAutomationStatusEnum.ACTIVE]: [
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.RUNNING,
        BusinessAutomationStatusEnum.FAILED,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.PARTIALLY_CANCELLED,
    ],
    [BusinessAutomationStatusEnum.RUNNING]: [
        BusinessAutomationStatusEnum.COMPLETED,
        BusinessAutomationStatusEnum.FAILED,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.PARTIALLY_CANCELLED,
    ],
    [BusinessAutomationStatusEnum.COMPLETED]: [
        BusinessAutomationStatusEnum.FAILED,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.FAILED]: [BusinessAutomationStatusEnum.ARCHIVED],
    [BusinessAutomationStatusEnum.CANCELLED]: [BusinessAutomationStatusEnum.ARCHIVED],
    [BusinessAutomationStatusEnum.PARTIALLY_CANCELLED]: [BusinessAutomationStatusEnum.ARCHIVED],
    [BusinessAutomationStatusEnum.ARCHIVED]: [],
};
/** Human-readable labels for status dropdowns (API uses lowercase enum values). */
export const BUSINESS_AUTOMATION_STATUS_LABELS = {
    [BusinessAutomationStatusEnum.DRAFT]: 'Draft',
    [BusinessAutomationStatusEnum.ACTIVE]: 'Active',
    [BusinessAutomationStatusEnum.RUNNING]: 'Running',
    [BusinessAutomationStatusEnum.COMPLETED]: 'Completed',
    [BusinessAutomationStatusEnum.FAILED]: 'Failed',
    [BusinessAutomationStatusEnum.CANCELLED]: 'Cancelled',
    [BusinessAutomationStatusEnum.PARTIALLY_CANCELLED]: 'Partially Cancelled',
    [BusinessAutomationStatusEnum.SENDING]: 'Sending',
    [BusinessAutomationStatusEnum.ARCHIVED]: 'Archived',
};
/**
 * Returns status options valid for the UI dropdown: current status plus all allowed transitions.
 * Use the appropriate map based on trigger type so users only see options the backend will accept.
 * Safe to use in frontend; does not depend on @reach/database or @reach/common.
 */
export function getValidStatusOptionsForUi(currentStatus, triggerType) {
    const normalized = (currentStatus || BusinessAutomationStatusEnum.DRAFT).toLowerCase();
    const map = triggerType === BusinessAutomationTriggerTypeEnum.ONE_TIME
        ? ONE_TIME_STATE_TRANSITIONS
        : RECURRING_STATE_TRANSITIONS;
    // Map is keyed by enum but API passes string; enum values are strings at runtime
    const transitions = map[normalized];
    const options = new Set([normalized]);
    if (transitions) {
        for (const t of transitions)
            options.add(t);
    }
    return Array.from(options).map(value => ({
        value,
        label: BUSINESS_AUTOMATION_STATUS_LABELS[value] ?? value,
    }));
}
export var BusinessAutomationTriggerTypeEnum;
(function (BusinessAutomationTriggerTypeEnum) {
    /**
     * This is special, it means that the automation will only run once and then be completed
     */
    BusinessAutomationTriggerTypeEnum["ONE_TIME"] = "one_time";
    BusinessAutomationTriggerTypeEnum["TRIGGER_BASED"] = "trigger_based";
    BusinessAutomationTriggerTypeEnum["DATE_BASED"] = "date_based";
})(BusinessAutomationTriggerTypeEnum || (BusinessAutomationTriggerTypeEnum = {}));
export var BusinessAutomationActionTypeEnum;
(function (BusinessAutomationActionTypeEnum) {
    BusinessAutomationActionTypeEnum["SEND_COMMUNICATION"] = "send_communication";
    /**
     * Wait until the next available time today, or skip if the time has already passed
     *
     * Behavior:
     * - If target time (e.g. 9AM) is in the future today → wait until that time
     * - If target time has already passed today → skip the action (return null)
     * - Skip just means move on to the next action, this requirement is met
     *
     * This is different from WAIT_UNTIL_NEXT_OCCURRENCE which always waits for the next
     * occurrence, even if it means waiting until tomorrow.
     *
     * Example: If it's 11AM and target is 9AM, this action will be skipped.
     * Example: If it's 7AM and target is 9AM, this action will wait until 9AM today.
     */
    BusinessAutomationActionTypeEnum["WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME"] = "wait_until_next_fixed_time";
    /**
     * Wait until the next occurrence of a specific time, always waiting for the next day if time has passed
     * (e.g. if it's 8:01 AM and target is 8:00 AM, wait until 8:00 AM tomorrow)
     */
    BusinessAutomationActionTypeEnum["WAIT_UNTIL_NEXT_OCCURRENCE"] = "wait_until_next_occurrence";
    BusinessAutomationActionTypeEnum["WAIT_FOR_DURATION"] = "wait_for_duration";
    /**
     * Exits automation early if user is no longer in segment
     */
    BusinessAutomationActionTypeEnum["CHECK_SEGMENT_MEMBERSHIP"] = "check_segment_membership";
})(BusinessAutomationActionTypeEnum || (BusinessAutomationActionTypeEnum = {}));
/**
 * Maximum allowed total delay for a WAIT_FOR_DURATION action. Enforced on
 * automation create/update and on automation template create/update; the
 * partner dashboard mirrors this in the wait-step UI.
 */
export const MAX_AUTOMATION_DELAY_DAYS = 365;
export function calculateTotalDelayInDays(duration) {
    const { days = 0, hours = 0, minutes = 0, seconds = 0 } = duration;
    return days + hours / 24 + minutes / (24 * 60) + seconds / (24 * 60 * 60);
}
export var TimezoneEnum;
(function (TimezoneEnum) {
    TimezoneEnum["EST"] = "America/New_York";
})(TimezoneEnum || (TimezoneEnum = {}));
export var MonthDayTriggerTypeEnum;
(function (MonthDayTriggerTypeEnum) {
    MonthDayTriggerTypeEnum["FIXED_DATE"] = "fixed_date";
})(MonthDayTriggerTypeEnum || (MonthDayTriggerTypeEnum = {}));
export var EventFilterTypeEnum;
(function (EventFilterTypeEnum) {
    EventFilterTypeEnum["SEGMENT_ID"] = "segment_id";
})(EventFilterTypeEnum || (EventFilterTypeEnum = {}));
export var ArtifactOutputKeyEnum;
(function (ArtifactOutputKeyEnum) {
    ArtifactOutputKeyEnum["USER_ID"] = "userId";
})(ArtifactOutputKeyEnum || (ArtifactOutputKeyEnum = {}));
export const oneTimeTriggerMetadataSchema = z.object({
    triggerType: z.literal(BusinessAutomationTriggerTypeEnum.ONE_TIME),
    scheduledAt: z.union([
        z.coerce
            .date()
            .describe('String in ISO 8601 format with timezone information (e.g., "2024-03-20T15:00:00-07:00" or "2024-03-20T15:00:00Z")'),
        z.literal('now').describe('Send as soon as the automation is marked active'),
    ]),
    timezone: z
        .string()
        .optional()
        .describe('Timezone in IANA format (e.g., "America/New_York"). This is used for auditing and frontend display. Times are automatically converted to UTC when stored.'),
});
export const eventBasedTriggerMetadataSchema = z.object({
    triggerType: z.literal(BusinessAutomationTriggerTypeEnum.TRIGGER_BASED),
    /**
     * @see ReachEventNamesEnum for built-in event names
     */
    eventName: z
        .string()
        .describe('Name of the event that starts a run. "USER_ENTERS_SEGMENT" is the only supported value today, and it is case sensitive.'),
    eventFilter: z
        .record(z.union([z.string(), z.literal(EventFilterTypeEnum.SEGMENT_ID)]), z.string())
        .describe('Narrows which occurrences of eventName start a run, keyed by the event field to match. For USER_ENTERS_SEGMENT use { "segment_id": "<segment uuid>" }. Use {} for no filter.'),
    cooldownSeconds: z
        .number()
        .describe('How long, in seconds, before the same customer can enter this automation again. 86400 is one day and 31556952 is one year. Use 0 for no cooldown, and -1 to never let the same customer enter again.'),
    artifactOutputKeys: z
        .array(z.string())
        .describe('Fields copied off the event and handed to the actions. Use ["userId"], which is what every automation needs so the actions know which customer the run is for.'),
    triggerIdKey: z
        .string()
        .describe('The field on the event whose value identifies one run, used to deduplicate. Use "userId".'),
    rootActionId: z
        .string()
        .describe('currentActionId of the action the run starts at. Must be exactly the first entry of the action array.'),
    customTriggerCopy: z
        .string()
        .optional()
        .describe('Optional copy to use when displaying the trigger in the frontend'),
});
const baseActionMetadataSchema = z.object({
    currentActionId: z
        .string()
        .optional()
        .describe('Unique id for this action within the automation, generated by the caller. Required once there is more than one action.'),
    nextActionId: z
        .string()
        .optional()
        .describe('currentActionId of the action that runs after this one. Omit on the last action. Actions form a single straight chain from the first action; every action must be reachable along it.'),
    templateActionId: z.string().uuid().optional(),
});
export const sendCommunicationActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.SEND_COMMUNICATION),
    communicationGroupId: z.string(),
    customBlockText: z.string().optional(),
    reputationTemplateType: z.nativeEnum(ReputationTemplateTypeEnum).optional(),
});
export const waitUntilFixedTimeActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME),
    hour: z.number(),
    minute: z.number(),
    timezone: z.nativeEnum(TimezoneEnum),
});
export const waitUntilNextOccurrenceActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE),
    hour: z.number(),
    minute: z.number(),
    timezone: z.nativeEnum(TimezoneEnum),
});
export const delayByDurationActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.WAIT_FOR_DURATION),
    days: z.number().min(0).default(0),
    hours: z.number().min(0).default(0),
    minutes: z.number().min(0).default(0),
    seconds: z.number().min(0).default(0),
});
export const checkSegmentMembershipActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.CHECK_SEGMENT_MEMBERSHIP),
    segmentIds: z.array(z.string().uuid()),
});
export const actionMetadataSchema = z.discriminatedUnion('actionType', [
    sendCommunicationActionMetadataSchema,
    waitUntilFixedTimeActionMetadataSchema,
    waitUntilNextOccurrenceActionMetadataSchema,
    delayByDurationActionMetadataSchema,
    checkSegmentMembershipActionMetadataSchema,
]);
export const singleActionData = z.object({
    actionType: z.nativeEnum(BusinessAutomationActionTypeEnum),
    actionMetadata: actionMetadataSchema,
});
export const actionDataSchema = z.array(singleActionData);
export const dateBasedTriggerMetadataSchema = z.object({
    triggerType: z.literal(BusinessAutomationTriggerTypeEnum.DATE_BASED),
    monthDayTriggerType: z.nativeEnum(MonthDayTriggerTypeEnum),
    dayToTrigger: z.number().describe('Day of the month to trigger on'),
    monthToTrigger: z.number().describe('Month to trigger on'),
    customTriggerCopy: z
        .string()
        .optional()
        .describe('Optional copy to use when displaying in the frontend'),
    rootActionId: z
        .string()
        .describe('currentActionId of the action the run starts at. Must be exactly the first entry of the action array.'),
});
// automation-template.ts extends these members into its own union rather than reusing this one.
export const triggerMetadataSchema = z.discriminatedUnion('triggerType', [
    oneTimeTriggerMetadataSchema,
    eventBasedTriggerMetadataSchema,
    dateBasedTriggerMetadataSchema,
]);
/** Public create payload for a custom business automation (HTTP API and MCP). */
export const createBusinessAutomationRequestSchema = z.object({
    name: z.string().describe('A friendly name for the automation'),
    description: z.string().optional().describe('A description for the automation'),
    trigger: triggerMetadataSchema
        .optional()
        .describe('Optional; the trigger may be set later. triggerType must equal type. For trigger_based, artifactOutputKeys must include "userId" and rootActionId must equal the first action currentActionId.'),
    type: z
        .nativeEnum(BusinessAutomationTriggerTypeEnum)
        .describe('one_time sends once at a scheduled moment; trigger_based runs whenever a customer causes an event; date_based runs every year on a given month and day.'),
    include_segments: z
        .array(z.string().uuid())
        .optional()
        .describe('IDs of existing segments to include. Recurring automations default to all customers when omitted.'),
    exclude_segments: z
        .array(z.string().uuid())
        .optional()
        .describe('IDs of existing segments to exclude'),
    action: actionDataSchema
        .optional()
        .describe('Optional; the steps may be set later. In run order, starting with the first: link each step to the next with currentActionId and nextActionId, and the last step has no nextActionId. send_communication actions need a real communicationGroupId.'),
});
export const automationContentChannelSchema = z.enum(['email', 'sms']);
export const generateAutomationContentToolParametersSchema = z.object({
    instructions: z
        .string()
        .trim()
        .min(1)
        .max(4000)
        .describe("The agent's content-generation brief derived from the user's request, including the offer, tone, call to action, and any merge-field tokens to preserve exactly."),
    channels: z
        .array(automationContentChannelSchema)
        .min(1)
        .max(2)
        .refine(channels => new Set(channels).size === channels.length, {
        message: 'channels must not contain duplicates',
    })
        .describe('The sending channels to generate in one communication group.'),
});
/**
 * The automation-builder agent's edit signature. Every field but the id is optional: an omitted
 * field keeps its stored value, a provided one replaces it wholesale.
 */
export const updateBusinessAutomationAgentRequestSchema = z.object({
    automationId: z.string().uuid().describe('The id of the draft automation to update'),
    name: z.string().optional().describe('A friendly name for the automation'),
    description: z.string().optional().describe('A description for the automation'),
    trigger: triggerMetadataSchema
        .optional()
        .describe('Replacement trigger for the automation. triggerType must match type. For trigger_based, rootActionId must equal the first action currentActionId, and at least one send_communication action is required.'),
    type: z
        .nativeEnum(BusinessAutomationTriggerTypeEnum)
        .optional()
        .describe('The type of automation: one_time, trigger_based, or date_based'),
    include_segments: z
        .array(z.string().uuid())
        .optional()
        .describe('Replacement list of segment IDs to include. An empty array clears the audience.'),
    exclude_segments: z
        .array(z.string().uuid())
        .optional()
        .describe('Replacement list of segment IDs to exclude. An empty array clears the exclusions.'),
    action: actionDataSchema
        .optional()
        .describe('Replacement action graph. send_communication actions need a real communicationGroupId. Link steps with currentActionId and nextActionId.'),
});
/** Closed union of the Campaign Performance tabs (Automations vs one-time Broadcasts). Single source of truth shared by the API query schema, common service, and the admin UI. */
export const campaignPerformanceTypeSchema = z.enum(['automations', 'broadcasts']);
/** One row of the Campaign Performance tables. Single source of truth for the API response, common service, and admin UI. Dates are ISO strings (the JSON wire format); rates are 0–1 or null when the denominator is zero. */
export const campaignPerformanceRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.nativeEnum(BusinessAutomationStatusEnum),
    triggerType: z.nativeEnum(BusinessAutomationTriggerTypeEnum),
    channels: z.array(z.enum(['email', 'sms'])),
    email: z.object({
        sent: z.number(),
        delivered: z.number(),
        openRate: z.number().nullable(),
        clicks: z.number(),
        bounceRate: z.number().nullable(),
        spamRate: z.number().nullable(),
        unsubRate: z.number().nullable(),
    }),
    sms: z.object({
        sent: z.number(),
        delivered: z.number(),
        failed: z.number(),
    }),
    sales: z.object({
        totalRevenue: z.number(),
        totalOrders: z.number(),
    }),
    scheduledAt: z.string().nullable().optional(),
    updatedAt: z.string().datetime().optional(),
});
export const hasBroadcastExperienceResponseSchema = z.object({
    hasBroadcastExperience: z
        .boolean()
        .describe('Whether the business has enough broadcast history to be past email best-practices guidance'),
});
export const businessAutomationSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    name: z.string(),
    status: z.nativeEnum(BusinessAutomationStatusEnum),
});
export const pausedAutomationSchema = businessAutomationSchema.extend({
    pausedAt: z.string().datetime(),
    lastReleaseAt: z.string().datetime().nullable(),
    pendingCount: z.number().int(),
    releasedCount: z.number().int(),
    resolvedCount: z.number().int(),
    hardBounces: z.number().int(),
    softBounces: z.number().int(),
    bounceMetrics: z.array(bounceMetricReadingSchema),
    hasActivePacingLoop: z.boolean(),
});
//# sourceMappingURL=business-automation.js.map